import {
    BaseFormComponent,
    ConfigurableCompsSelectComponent,
    CUSTOM_ERROR_TYPE,
    ExpandedImageUploadComponent,
    FormType,
    FormValues,
    SelectComponent,
    ValidationRule,
    ValidationRuleLevel,
    ValidationRuleType
} from "./DynamicFormTypes.ts";
import {UseFormReturn} from "react-hook-form";
import {defaultParserForSelects, getImageTypeDisplay, parseRuleType, setRuleType} from "./dyanimcFormUtil.tsx";
import {createLocalDate, formatDateNumeric, formatDateToString} from "../../utils/dateUtils.ts";
import {AggregateValidator} from "./fields/customvalidator/AggregateValidator.ts";
import {FormImageRequirement, FormImageType, ImageSubmission} from "../../api/ApiTypes.ts";

export class ValidationFactory {


    static createValidationRules(
        component: BaseFormComponent,
        metaData: any,
        formMethods: UseFormReturn<FormValues>,
        flattenedSchema: Map<string, BaseFormComponent>,
        customValidator: AggregateValidator | undefined,
    ): any {
        const hookFormRules: any = {};
        // let x = true;
        // if (x) {
        //     return hookFormRules;
        // }
        if (!component) {
            return hookFormRules;
        }
        const hasIncludedRules: boolean = (component?.validationRules?.length ?? 0) > 0;
        const needsCustomValidation: boolean = customValidator !== undefined && customValidator.isFieldIncluded(component.fieldKey);
        //if it doesnt have any rules and it doesnt need to trigger custom validator, return empty object
        if (!hasIncludedRules && !needsCustomValidation) {
            return hookFormRules;
        }

        type RHFValidateResult = boolean | string;
        type RHFValidateFn = (value: any) => RHFValidateResult | Promise<RHFValidateResult>;

        const validateFunctions: Record<string, RHFValidateFn> = {};

            // Add a SINGLE manual error check that runs FIRST
        validateFunctions[`${ValidationRuleLevel.ERROR}_${CUSTOM_ERROR_TYPE.MANUAL}_dont_override_manual_error`] = (_value: any) => {
            const fieldState = formMethods.getFieldState(component.fieldKey);
            const errorInfo = parseRuleType(fieldState?.error?.type);
            if (errorInfo.type === CUSTOM_ERROR_TYPE.MANUAL && errorInfo.level === ValidationRuleLevel.ERROR) {
                //make info log this log if you want to see when errors are being set via the validator (the one via the schema)
                console.debug("Detected error outside of factory, returning error as is for", component.fieldKey);
                return fieldState.error?.message ?? "";
            }
            return true; // No manual error, continue with other validations
        };

        validateFunctions[`${ValidationRuleLevel.WARNING}_${CUSTOM_ERROR_TYPE.MANUAL}_dont_override_manual_warning`] = (_value: any) => {
            const fieldState = formMethods.getFieldState(component.fieldKey);
            const errorInfo = parseRuleType(fieldState?.error?.type);
            if (errorInfo.type === CUSTOM_ERROR_TYPE.MANUAL && errorInfo.level === ValidationRuleLevel.WARNING) {
                //make info log this log if you want to see when errors are being set via the validator (the one via the schema)
                console.debug("Detected error outside of factory, returning error as is for", component.fieldKey);
                return fieldState.error?.message ?? "";
            }
            return true; // No manual error, continue with other validations
        };


        component.validationRules?.forEach((rule, index) => {
            const ruleKey = this.getReactHookFormRuleKey(rule, index);
            validateFunctions[ruleKey] = this.createValidatorFunction(rule, component, metaData, formMethods, flattenedSchema);
        })


        if (customValidator) {
            //this is just a DUMMY FUNCTION to trigger the custom validator, the actual validation is done inside the function (async)
            validateFunctions[`${ValidationRuleLevel.WARNING}_${CUSTOM_ERROR_TYPE.FACTORY}_custom_validator`] = async (_value: any) => {
                const result = await customValidator.validateBest(component.fieldKey, formMethods);
                if (result && result.level === ValidationRuleLevel.WARNING) {
                    return result.message || "Validation warning from custom validator";
                }
                return true; // this is just to trigger custom validation, as opposed to watching all the fields individually
            };
            validateFunctions[`${ValidationRuleLevel.ERROR}_${CUSTOM_ERROR_TYPE.FACTORY}_custom_validator`] = async (_value: any) => {
                const result = await customValidator.validateBest(component.fieldKey, formMethods);
                if (result && result.level === ValidationRuleLevel.ERROR) {
                    return result.message || "Validation error from custom validator";
                }
                return true; // this is just to trigger custom validation, as opposed to watching all the fields individually
            };
        }

        if (Object.keys(validateFunctions).length > 0) {
            hookFormRules.validate = validateFunctions;
        }

        return hookFormRules;
    }

    //allows us to have as many rules of the same type as we want, so we can have multiple required rules, or multiple min rules, etc.
    private static getReactHookFormRuleKey(rule: ValidationRule, ruleIndex: number) :string {
        const baseType = rule.type.toString();
        return `${setRuleType(rule.level, CUSTOM_ERROR_TYPE.FACTORY)}_${baseType}_${ruleIndex}`;
    }


    //todo some of these rules probably need to account for the selects being objects now
    private static createValidatorFunction(rule: ValidationRule, component: BaseFormComponent, metaData: any, formMethods: UseFormReturn<FormValues>, flattenedSchema: Map<String, BaseFormComponent>): (value: any) => string | boolean {
        const {type, params, message} = rule;

        return (value: any) => {
            switch (type) {
                case ValidationRuleType.REQUIRED: {
                    if (component.type == FormType.SELECT) {
                        const selectComponent = component as SelectComponent;
                        if (selectComponent.provided) {
                            const parsedValue = defaultParserForSelects(value);
                            if (parsedValue && typeof parsedValue == "object" && "value" in parsedValue) {
                                const values = selectComponent.provided.map(value => value.value)
                                if (!values.includes(parsedValue.value)) {
                                    return "This field is required";
                                }
                            }
                        }
                    }
                    if (component.type == FormType.CHECKBOX) {
                        if (value === null) return "This field is required";
                        if (value === false) {
                            return message || "This box must be checked";
                        }
                    }
                    if (component.type == FormType.EXPANDED_IMAGE_UPLOAD) {
                        const expandedImageComponent = component as ExpandedImageUploadComponent;
                        if (expandedImageComponent.requiredTypes) {
                            const list = [];

                            for (const requirement of expandedImageComponent.requiredTypes as FormImageRequirement[]) {

                                // Check if we should include this type based on evaluator
                                if (requirement.evaluator && requirement.watchFieldKey) {
                                    const fieldValue = typeof defaultParserForSelects(formMethods.getValues(requirement.watchFieldKey)) == "object" ? defaultParserForSelects(formMethods.getValues(requirement.watchFieldKey)).value : defaultParserForSelects(formMethods.getValues(requirement.watchFieldKey));
                                    if (fieldValue !== requirement.evaluator) {
                                        continue;
                                    }
                                }

                                // Calculate the total multiplier
                                const multiplier = ValidationFactory.getRequirementCount(requirement, formMethods);

                                // Add the type 'multiplier' times
                                for (let i = 0; i < multiplier; i++) {
                                    list.push(requirement.type);
                                }
                            }

                            if (Array.isArray(value)) {
                                for (const uploadedImage of value as ImageSubmission[]) {
                                    if (FormImageType.NONE == uploadedImage.imageType) {
                                        return "At least one uploaded image does not have a label."
                                    }
                                    if (list.includes(uploadedImage.imageType)) {
                                        list.splice(list.lastIndexOf(uploadedImage.imageType), 1);
                                    }
                                }
                            }

                            if (list.length > 0) {
                                return `At least one image requirement not met: ${list.map(type => getImageTypeDisplay(type)).join(', ')}`
                            }
                        }


                    }
                    return this.hasValue(value) || (message || `This field is required`);
                }
                case ValidationRuleType.MIN: {
                    if (!this.hasValue(value)) {
                        return true;
                    }

                    if (typeof params === "object") {

                        if ("type" in params) {
                            if ("DATE" === params.type) {
                                const lowerLimit: Date = createLocalDate(null);
                                const paramValue = parseInt(params.value);
                                switch (params.timeUnit) {
                                    case "YEARS":
                                        lowerLimit.setFullYear(lowerLimit.getFullYear() + paramValue);
                                        break;
                                    case "MONTHS":
                                        lowerLimit.setMonth(lowerLimit.getMonth() + paramValue);
                                        break;
                                    case "DAYS":
                                        lowerLimit.setDate(lowerLimit.getDate() + paramValue);
                                        break;
                                    case "HOURS":
                                        lowerLimit.setHours(lowerLimit.getHours() + paramValue);
                                        break;
                                }

                                const evaluatedMinDate = createLocalDate(lowerLimit);
                                const submittedDate = createLocalDate(value)
                                if (submittedDate.getTime() < evaluatedMinDate.getTime()) {
                                    return `Date is more than ${Math.abs(paramValue)} ${params.timeUnit.toLowerCase()} prior to the current date`;
                                }
                            }
                            else if ("WORD_COUNT" === params.type) {
                                const paramValue = parseInt(params.value);
                                const wordCount = this.determineWordCount(value);
                                console.log("Word count for value:", wordCount);
                                if (wordCount < paramValue) {
                                    return (message || `This field must have at least ${paramValue} words`);
                                }
                                return true;
                            }
                        }
                    }
                    else {

                        const minValue = parseFloat(params);
                        if (Array.isArray(value)) {
                            return value.length >= minValue || (message || `This field must have at least ${minValue} items`);
                        }
                        if (this.isNumeric(value)) {
                            return parseFloat(value) >= minValue || (message || `This field must be at least ${minValue}`);
                        }
                        if (typeof value === 'string') {
                            return value.length >= minValue || (message || `This field must be at least ${minValue} characters long`);
                        }
                    }
                    return true;
                }
                case ValidationRuleType.MAX: {
                    if (!this.hasValue(value)) {
                        return true; // If the value is not present, we assume it passes (its a max rule, not a min)
                    }
                    const maxValue = parseFloat(params);
                    if (Array.isArray(value)) {
                        return value.length <= maxValue || (message || `This field must have at most ${maxValue} items`);
                    }
                    if (this.isNumeric(value)) {
                        return parseFloat(value) <= maxValue || (message || `This field must be at most ${maxValue}`);
                    }
                    if (typeof value === 'string') {
                        return value.length <= maxValue || (message || `This field must be at most ${maxValue} characters long`);
                    }
                    return true;
                }
                case ValidationRuleType.PATTERN: {
                    if (!this.hasValue(value)) {
                        return true;
                    }
                    try {
                        const regex = new RegExp(params);
                        return regex.test(value) || (message || `Invalid format`);
                    } catch (e) {
                        console.error("Invalid regex pattern in validation rule:", params, e);
                        return "Invalid validation pattern configured";
                    }
                }
                case ValidationRuleType.NOT_EQUALS_META: {
                    const otherFieldValue = metaData[params];
                    if (value !== otherFieldValue) {
                        return true; // If the value is not equal to the meta data, validation passes
                    }
                    return (message || `This field must not equal ${otherFieldValue}`); // Return the error message if validation fails
                }
                case ValidationRuleType.NOT_EQUALS_FIELD: {
                    const otherFieldValue = formMethods.getValues(params);
                    if (value !== otherFieldValue) {
                        return true; // If the value is not equal to the other field, validation passes
                    }
                    return (message || `This field must not equal ${otherFieldValue}`);
                }
                case ValidationRuleType.EQUALS_FIELD: {
                    const otherFieldValue = formMethods.getValues(params);
                    if (value === otherFieldValue) {
                        return true; // If the value is equal to the other field, validation passes
                    }
                    return (message || `This field should equal ${otherFieldValue}`);
                }
                case ValidationRuleType.RISK_RATING_VALUE: {
                    const riskRatingRaw = formMethods.getValues("riskRating");
                    const riskRating = defaultParserForSelects(riskRatingRaw);
                    if (!riskRating || !riskRating.value) {
                        return "Risk Rating is required.";
                    }

                    if (!this.hasValue(value) || !this.isNumeric(value)) {
                        return "Value is required."
                    }

                    if ((riskRating.value === "Excellent" || riskRating.value === "Reasonable" || riskRating.value === "High Risk") && value == 0) {
                        return "The value conclusion must be greater than zero for Excellent, Reasonable, or High Risk ratings; review and revise as needed.";
                    }
                    return true;
                }
                case ValidationRuleType.RISK_RATING_VARIANCE: {
                    const riskRatingRaw = formMethods.getValues("riskRating");
                    const riskRating = defaultParserForSelects(riskRatingRaw);
                    if (!riskRating || !riskRating.value) {
                        return "Risk Rating is required."
                    }

                    if (!this.hasValue(value) || !this.isNumeric(value)) {
                        return "Value is required."
                    }
                    const valueConclusion = parseFloat(value);

                    const originalValueString = formMethods.getValues("originalAppraisalValue");
                    if (!this.hasValue(originalValueString) || !this.isNumeric(originalValueString)) {
                        return "Original Appraisal Value is required."
                    }
                    const originalValue = parseFloat(originalValueString);
                    const variance = ((valueConclusion - originalValue) * 100) / originalValue;
                    const varianceSmall = (variance < 10 && variance > -10);

                    if ((riskRating.value === "Excellent" || riskRating.value === "Reasonable") && (!varianceSmall)) {
                        return "Variance between appraisal value and review value exceeds 10%, please check that values were accurately entered or reconsider your quality rating.";
                    }
                    if ((riskRating.value === "High Risk" || riskRating.value === "Inconclusive") && (varianceSmall)) {
                        return "Review the value conclusion and risk rating. The value variance from the Original Appraisal is less than 10%; this would typically suggest a Reasonable Risk rating.";
                    }
                    return true;
                }
                case ValidationRuleType.RISK_RATING_SELECT_COMPARABLES: {
                    const riskRating = defaultParserForSelects(value);
                    if (!riskRating || !riskRating.value) {
                        return "Risk Rating is required."
                    }

                    const configurableCompSelectComponent: ConfigurableCompsSelectComponent | undefined = flattenedSchema.get(FormType.CONFIGURABLE_COMPS_SELECT.toString()) as ConfigurableCompsSelectComponent | undefined;

                    if (!configurableCompSelectComponent) {
                        return "No component found in the form needed to pass warning.";
                    }
                    let compsCount = 0;
                    configurableCompSelectComponent.compsDataGridInfos.forEach(dataGridInfo => {
                        const compsSubmission = formMethods.getValues(dataGridInfo.dataGridFieldKey);
                        compsCount += (compsSubmission && Array.isArray(compsSubmission)) ? compsSubmission.length : 0;
                    })

                    if ((riskRating.value === "Inconclusive" || riskRating.value == "High Risk") && compsCount < 2) {
                        return "You did not select 2 additional comparables to support your conclusion. If no additional comps are available or the best comps were used in the original appraisal; provide commentary."
                    }

                    return true;
                }
                case ValidationRuleType.RISK_RATING_CONCLUSION: {
                    const riskRating = defaultParserForSelects(value);
                    if (!riskRating || !riskRating.value) {
                        return "Risk Rating is required."
                    }

                    if (riskRating.value === "Inconclusive" || riskRating.value == "High Risk") {
                        return "Risk Rating is selected as " + riskRating.label + ". Make sure to explain your Value Conclusion in commentary."
                    }
                    return true;
                }
                case ValidationRuleType.SUBJECT_PROPERTY_MIXED_USE_OR_COMMERCIAL: {
                    if ("yes" === value) {
                        return "If the Subject is Mixed-use or commercial, please stop and escalate this case to SVIbrokerquality@stewart.com";
                    }
                    return true;
                }
                case ValidationRuleType.SUBJECT_PROPERTY_INSPECTIONS_DATE_VALID: {
                    if (!metaData.orderDetails?.appraisal?.createDate) {
                        return "Error with order could not look up Ordered Date, Please contact us"
                    }

                    if (!value || "" === value.trim()) {
                        return "Please provide an inspection date";
                    }

                    const inspectionDate = createLocalDate(formMethods.getValues("inspectionDate"));
                    const stringOrderedDate: string = metaData.orderDetails.appraisal.createDate;
                    const orderedDate = createLocalDate(stringOrderedDate);
                    if (isNaN(inspectionDate.getTime())) {
                        return "Please provide an inspection date";
                    }
                    if (isNaN(orderedDate.getTime())) {
                        return "Error with order could not look up Ordered Date, Please contact us";
                    }

                    if (inspectionDate < orderedDate) {
                        return `Inspection date cannot be before ${formatDateNumeric(stringOrderedDate)}`;
                    }

                    let now: Date = createLocalDate(null);

                    if (inspectionDate.getTime() > now.getTime()) {
                        return "Inspection date must be prior to or equal to the current date";
                    }

                    return true;
                }
                case ValidationRuleType.PERCENT_OCCUPIED_VACANCY: {
                    const ownerOccupied = formMethods.getValues("ownerOccupied");
                    const tenantOccupied = formMethods.getValues("tenantOccupied");
                    const vacant = formMethods.getValues("vacant");

                    // ensure we have a value for each before we decide if they are bad
                    if (!ownerOccupied || !tenantOccupied || !vacant) {
                        return true;
                    }

                    let total = Number(ownerOccupied) + Number(tenantOccupied) + Number(vacant);
                    if (total !== 100) {
                        return "Percent Owner Occupied, Percent Tenant Occupied and Percent Vacancy must equal 100%";
                    }

                    return true;
                }
                case ValidationRuleType.NEIGHBORHOOD_PRICE_RANGE: {

                    const asIsListPrice90: number = Number(formMethods.getValues("asIsListPrice90"));
                    const lowRange: number = Number(formMethods.getValues("neighborhoodPriceRangeLow"));
                    const highRange: number = Number(formMethods.getValues("neighborhoodPriceRangeHigh"));

                    // if we cannot do the comparison then it is still good
                    if (isNaN(asIsListPrice90) || isNaN(lowRange) || isNaN(highRange)) {
                        return true;
                    }

                    if (asIsListPrice90 <= lowRange || asIsListPrice90 >= highRange) {
                        return "The as is suggested list price must be between the Neighborhood price range high and low";
                    }

                    return true;
                }
                case ValidationRuleType.NAUGHTY_WORDS: {
                    //deprecating this, it will be auto ran on all text areas
                    return true;
                }
                case ValidationRuleType.LESS_THAN: {
                    if (!this.hasValue(value)) {
                        return true;
                    }

                    if (component.type === FormType.SIMPLE_DATE) {

                        let upperLimit: Date = createLocalDate(null);
                        if (this.hasValue(params)) {
                            upperLimit = createLocalDate(params)
                        }

                        const valueDate = createLocalDate(value);
                        if (valueDate.getTime() >= upperLimit.getTime()) {
                            return `Date must be prior to ${formatDateNumeric(formatDateToString(upperLimit))}`
                        }
                    }

                    return true;
                }
                case ValidationRuleType.GREATER_THAN_OR_EQUAL: {
                    if (!this.hasValue(value)) {
                        return true;
                    }

                    if (FormType.SIMPLE_DATE === component.type) {
                        let lowerLimit: Date = createLocalDate(null);
                        let labelOfWatchedField = '';
                        if (params?.fieldKey) {
                            let watchedDate: string = formMethods.getValues(params.fieldKey);
                            lowerLimit = createLocalDate(watchedDate);
                            const watchedComponent: BaseFormComponent | undefined = flattenedSchema.get(params.fieldKey);
                            if (watchedComponent) {
                                labelOfWatchedField = watchedComponent.label;
                            }

                        }
                        const valueDate = createLocalDate(value);
                        if (valueDate.getTime() < lowerLimit.getTime()) {
                            return `Date must be after ${labelOfWatchedField} ${formatDateNumeric(formatDateToString(lowerLimit))}`
                        }
                    }

                    return true;
                }
                case ValidationRuleType.SUBJECT_CONDITION: {
                    if (!this.hasValue(value)) {
                        return true;
                    }

                    let selection = JSON.parse(value);
                    const year = new Date().getFullYear();
                    const subjectPropertyYear = parseInt(formMethods.getValues("subjectPropertyYearBuilt"));
                    const formattedValue = selection.value.toLowerCase()
                    if ("excellent" === formattedValue && ((year - 5) > subjectPropertyYear)) {
                        return "In order for the subject condition rating to be rated \"excellent\", the property  must be less than 5 years old."
                    }
                    else if ("fair" === formattedValue) {
                        return `Comment on the subject's condition rating of fair`;
                    }
                    else if ("poor" === formattedValue) {
                        return `Comment on the subject's condition rating of poor`;
                    }
                    else if ("average" === formattedValue) {
                        return `Average condition typically implies minor wear & tear and/or cosmetic repair items are present. Please consider adding repairs, or comment as to why none are present.`;
                    }

                    return true;
                }
                case ValidationRuleType.TITLE_LEGAL_ISSUES: {
                    if (!this.hasValue(value)) {
                        return true;
                    }

                    if ("yes" === value) {
                        return `Because there are title/legal issues, additional comments are required.`
                    }

                    return true;
                }
                case ValidationRuleType.HEALTH_SAFETY_ISSUES: {
                    if (!this.hasValue(value)) {
                        return true;
                    }

                    if ("yes" === value) {
                        return `Provide detailed explanation of the health/safety issues.`
                    }

                    return true;
                }
                case ValidationRuleType.ENVIRONMENTAL_ISSUES: {
                    if (!this.hasValue(value)) {
                        return true;
                    }

                    if ("yes" === value) {
                        return `Provide details explanation of the environmental issues noted.`
                    }

                    return true;
                }
                case ValidationRuleType.IMMEDIATE_REPAIRS_REQUIRED: {
                    if (!this.hasValue(value)) {
                        return `This field is required`
                    }

                    if ("no" === value) {
                        return true;
                    }

                    let repairList: Array<any> = formMethods.getValues("exteriorRepairsList");
                    if (repairList.length < 1 || !repairList[0]?.damageType) {
                        return `You have indicated the subject is in need of immediate repairs, please fill out the repair costs section of the report accordingly`
                    }

                    return true;
                }
                case ValidationRuleType.AS_IS_LIST_PRICE: {
                    const asIsListPrice = Number(formMethods.getValues("asIsListPrice90"));

                    // required for checking withing neighborhood ranged
                    const lowRange: number = Number(formMethods.getValues("neighborhoodPriceRangeLow"));
                    const highRange: number = Number(formMethods.getValues("neighborhoodPriceRangeHigh"));
                    const validBracketRange: boolean = !isNaN(lowRange) && !isNaN(highRange) && !isNaN(asIsListPrice);

                    // check asislistprice within bracket
                    if (validBracketRange && (lowRange > asIsListPrice || highRange < asIsListPrice)) {
                        return `Fair Market As Is Suggested List Price is not in the Neighborhood price range.`;
                    }

                    // required for price compared to total price
                    const asRepairedListPrice: number = Number(formMethods.getValues("asRepairedListPrice90"));
                    const totalRepair: number = Number(formMethods.getValues("totalRepairs"));

                    const validListPrices: boolean = !isNaN(asIsListPrice) && !isNaN(asRepairedListPrice);
                    // need to handle list prices not the same but total repair cost is 0
                    if (validListPrices && asIsListPrice !== asRepairedListPrice && (isNaN(Number(totalRepair)) || totalRepair === 0)) {
                        return `The as is value must equal the as repaired value if the repair total is zero.`
                    }

                    return true;
                }
                case ValidationRuleType.AS_IS_LIST_PRICE_WARNING: {
                    const asIsListPrice: number = Number(formMethods.getValues("asIsListPrice90"));
                    const asIsSalePrice: number = Number(formMethods.getValues("asIsSalePrice90"));

                    const validValues: boolean = !isNaN(asIsListPrice) && !isNaN(asIsSalePrice) && asIsListPrice > 0;
                    if (validValues) {
                        const percentDifferent = Math.abs(asIsListPrice - asIsSalePrice) / asIsListPrice * 100;
                        if (percentDifferent > 30) {
                            return `The difference between the as is suggested list price and the as is suggested sale price is greater than 30%. Please provide an explanation.`;
                        }

                        if (asIsSalePrice > asIsListPrice) {
                            return `Warning Result - The suggested as is sale is higher than the suggested as is list. Please comment`;
                        }
                    }

                    return true;
                }
                case ValidationRuleType.AS_IS_SALE_PRICE: {
                    const asIsSalePrice: number = Number(formMethods.getValues("asIsSalePrice90"));

                    // required for checking withing neighborhood ranged
                    const lowRange: number = Number(formMethods.getValues("neighborhoodPriceRangeLow"));
                    const highRange: number = Number(formMethods.getValues("neighborhoodPriceRangeHigh"));
                    const validBracketRange: boolean = !isNaN(lowRange) && !isNaN(highRange) && !isNaN(asIsSalePrice);

                    if (validBracketRange && (lowRange > asIsSalePrice || highRange < asIsSalePrice)) {
                        return `Fair Market As Is Suggested Sale Price is not in the Neighborhood price range`
                    }

                    return true;
                }
                case ValidationRuleType.AS_IS_SALE_PRICE_WARNING: {
                    const asIsSalePrice: number = Number(formMethods.getValues("asIsSalePrice90"));
                    const asIsListPrice: number = Number(formMethods.getValues("asIsListPrice90"));

                    // sale price larger than list price
                    const validPrices: boolean = !isNaN(asIsListPrice) && !isNaN(asIsSalePrice);
                    if (validPrices) {
                        if (asIsSalePrice > asIsListPrice) {
                            return `The suggested as is sale is higher than the suggested as is list. Please comment`;
                        }
                    }

                    // percent difference too large
                    const lastSalePrice: number = Number(formMethods.getValues("lastSalePrice"))
                    const validSales = !isNaN(asIsSalePrice) && !isNaN(lastSalePrice);
                    if (validSales && asIsSalePrice !== 0) {
                        const percentDifference: number = Math.abs(lastSalePrice - asIsSalePrice) / asIsSalePrice * 100
                        if (percentDifference > 10) {
                            return `Your as-is Suggested Sale Price varies from the Subject's prior sale price by more than 10%. Please add commentary to support this variance, or reconsider pricing.`
                        }
                    }

                    return true;
                }
                case ValidationRuleType.AS_REPAIRED_LIST_PRICE: {
                    const asRepairedList: number = Number(formMethods.getValues("asRepairedListPrice90"));
                    const lowRange: number = Number(formMethods.getValues("neighborhoodPriceRangeLow"));
                    const highRange: number = Number(formMethods.getValues("neighborhoodPriceRangeHigh"));

                    const validBracketRange: boolean = !isNaN(lowRange) && !isNaN(highRange) && !isNaN(asRepairedList);
                    if (validBracketRange && (lowRange > asRepairedList || highRange < asRepairedList)) {
                        return `The as repaired suggested list price must be between the neighborhood price range high and low`;
                    }

                    return true;
                }
                case ValidationRuleType.AS_REPAIRED_LIST_PRICE_WARNING: {
                    const asRepairedList: number = Number(formMethods.getValues("asRepairedListPrice90"));
                    const asRepairedSale: number = Number(formMethods.getValues("asRepairedSalePrice90"));

                    const validPrices = !isNaN(asRepairedList) && !isNaN(asRepairedSale);
                    if (validPrices && (asRepairedSale > asRepairedList)) {
                        return `The suggested as repaired sale is higher than the suggested as repaired list. Please comment`;
                    }

                    return true;
                }

                case ValidationRuleType.AS_REPAIRED_SALE_PRICE: {
                    const asRepairedSale: number = Number(formMethods.getValues("asRepairedSalePrice90"));
                    const lowRange: number = Number(formMethods.getValues("neighborhoodPriceRangeLow"));
                    const highRange: number = Number(formMethods.getValues("neighborhoodPriceRangeHigh"));

                    const validBracketRange: boolean = !isNaN(lowRange) && !isNaN(highRange) && !isNaN(asRepairedSale);
                    if (validBracketRange && (lowRange > asRepairedSale || highRange < asRepairedSale)) {
                        return `The as repaired suggested sale price must be between the neighborhood price range high and low`;
                    }

                    return true;
                }

                case ValidationRuleType.BEDROOM_ADJUSTMENT: {
                    const bedroomAdjustment = parseInt(formMethods.getValues("bedroomAdjustmentAmount"));
                    if((bedroomAdjustment < 0) || (bedroomAdjustment > 10000)){
                        return `WarningResult - Verify the adjustment you have for bedroom. For this price range the typical figure is $0 - $10,000. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }
                    return true;
                }

                case ValidationRuleType.GARAGE_ADJUSTMENT: {
                    let averageSalePrice: number = 0;
                    const saleComps = formMethods.getValues(`datagridconfigurablecompsselectSale`);
                    let i = 0;
                    for(const saleComp of saleComps){
                        const price = saleComp.saleCompAdjustedPrice;
                        if(price !== null || !isNaN(price)){
                            const priceValue = parseInt(price);
                            averageSalePrice += priceValue;
                            i++;
                        }
                    }
                    const average = averageSalePrice / i;
                    const garageAdjustment = parseInt(formMethods.getValues("garageAdjustmentAmount"));
                    if((average > 0 && average < 200000) && (garageAdjustment < 500 || garageAdjustment > 1500)){
                        return `WarningResult - Verify the adjustment you have for garage. For this price range the typical figure is $500 - $1,500. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 200000 && average < 500000) && (garageAdjustment < 1500 || garageAdjustment > 5000)){
                        return `WarningResult - Verify the adjustment you have for garage. For this price range the typical figure is $1,500 - $5,000. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 500000 && average < 1000000) && (garageAdjustment <  5000 || garageAdjustment > 10000)){
                        return `WarningResult - Verify the adjustment you have for garage. For this price range the typical figure is $5,000 - $10,000. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 1000000) && (garageAdjustment <  5000)){
                        return `WarningResult - Verify the adjustment you have for garage. For this price range the typical figure is $5,000 - anything. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }
                    return true;
                }

                case ValidationRuleType.FULL_BATH_ADJUSTMENT: {
                    let averageSalePrice: number = 0;
                    const saleComps = formMethods.getValues(`datagridconfigurablecompsselectSale`);
                    let i = 0;
                    for(const saleComp of saleComps){
                        const price = saleComp.saleCompAdjustedPrice;
                        if(price !== null || !isNaN(price)){
                            const priceValue = parseInt(price);
                            averageSalePrice += priceValue;
                            i++;
                        }
                    }
                    const average = averageSalePrice / i;
                    const fullBathAdjustment = parseInt(formMethods.getValues("fullBathAdjustmentAmount"));
                    if((average > 0 && average < 200000) && (fullBathAdjustment < 1000 || fullBathAdjustment > 3000)){
                        return `WarningResult - Verify the adjustment you have for full bath. For this price range the typical figure is $1,000 - $3,000. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 200000 && average < 500000) && (fullBathAdjustment < 3000 || fullBathAdjustment > 5000)){
                        return `WarningResult - Verify the adjustment you have for full bath. For this price range the typical figure is $3,000 - $5,000. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 500000 && average < 1000000) && (fullBathAdjustment <  5000 || fullBathAdjustment > 10000)){
                        return `WarningResult - Verify the adjustment you have for full bath. For this price range the typical figure is $5,000 - $10,000. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 1000000) && (fullBathAdjustment <  10000)){
                        return `WarningResult - Verify the adjustment you have for full bath. For this price range the typical figure is $10,000 - anything. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }
                    return true;
                }

                case ValidationRuleType.HALF_BATH_ADJUSTMENT: {
                    let averageSalePrice: number = 0;
                    const saleComps = formMethods.getValues(`datagridconfigurablecompsselectSale`);
                    let i = 0;
                    for(const saleComp of saleComps){
                        const price = saleComp.saleCompAdjustedPrice;
                        if(price !== null || !isNaN(price)){
                            const priceValue = parseInt(price);
                            averageSalePrice += priceValue;
                            i++;
                        }
                    }
                    const average = averageSalePrice / i;
                    const halfBathAdjustment = parseInt(formMethods.getValues("halfBathAdjustmentAmount"));
                    if((average > 0 && average < 200000) && (halfBathAdjustment < 500 || halfBathAdjustment > 1500)){
                        return `WarningResult - Verify the adjustment you have for half bath. For this price range the typical figure is $1,000 - $3,000. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 200000 && average < 500000) && (halfBathAdjustment < 1500 || halfBathAdjustment > 2450)){
                        return `WarningResult - Verify the adjustment you have for half bath. For this price range the typical figure is $3,000 - $5,000. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 500000 && average < 1000000) && (halfBathAdjustment <  2450 || halfBathAdjustment > 5000)){
                        return `WarningResult - Verify the adjustment you have for half bath. For this price range the typical figure is $5,000 - $10,000. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 1000000) && (halfBathAdjustment <  5000)){
                        return `WarningResult - Verify the adjustment you have for half bath. For this price range the typical figure is $5,000 - anything. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }
                    return true;
                }

                case ValidationRuleType.LIVING_AREA_ADJUSTMENT: {
                    let averageSalePrice: number = 0;
                    const saleComps = formMethods.getValues(`datagridconfigurablecompsselectSale`);
                    let i = 0;
                    for(const saleComp of saleComps){
                        const price = saleComp.saleCompAdjustedPrice;
                        if(price !== null || !isNaN(price)){
                            const priceValue = parseInt(price);
                            averageSalePrice += priceValue;
                            i++;
                        }
                    }
                    const average = averageSalePrice / i;
                    const glaAdjustment = parseInt(formMethods.getValues("livingAdjustmentAmount"));
                    const gla = parseInt(formMethods.getValues("subjectPropertyGla"));
                    if((average > 0 && average < 200000) && (glaAdjustment < (gla) || glaAdjustment > (gla * 20))){
                        return `WarningResult - Verify the adjustment you have for living area. For this price range the typical figure is $1/sq ft - $20/sq ft. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 200000 && average < 500000) && (glaAdjustment < (gla * 20) || glaAdjustment > (gla * 50))){
                        return `WarningResult - Verify the adjustment you have for living area. For this price range the typical figure is $20/sq ft - $50/sq ft. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 500000 && average < 1000000) && (glaAdjustment <  (gla * 50) || glaAdjustment > (gla * 100))){
                        return `WarningResult - Verify the adjustment you have for half bath. For this price range the typical figure is $50/sq ft - $100/sq ft. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }

                    if((average > 1000000) && ((glaAdjustment < gla * 100) || (glaAdjustment > (gla * 250)))){
                        return `WarningResult - Verify the adjustment you have for half bath. For this price range the typical figure is $100/sq ft - $250/sq ft. Please reconsider the adjustment value accordingly, or comment on the market factors that necessitate this adjustment.`
                    }
                    return true;
                }

                default:
                    throw new Error(`Unknown validation rule type: ${type}`);
            }

        }
    }

    static determineWordCount(value: unknown): number {
        if (typeof value !== 'string') return 0;

        const trimmed = value.trim();
        if (!trimmed) return 0;

        return trimmed.split(/\s+/).length;
    }

    static getRequirementCount(requirement: FormImageRequirement, formMethods: UseFormReturn<FormValues>) {
        let multiplier = 1;

        if (requirement.hardCodedMultiplier) {
            multiplier = requirement.hardCodedMultiplier;
        }

        if (requirement.watchFieldKey && !requirement.evaluator) {
            const fieldValue = formMethods.getValues(requirement.watchFieldKey);
            multiplier = requirement.hardCodedMultiplier
                ? requirement.hardCodedMultiplier * fieldValue  // Multiply both if both exist
                : fieldValue;  // Otherwise just use field value
        }

        return multiplier;
    }

    static getMaxValue(component: BaseFormComponent, defaultValue: number | undefined): number | undefined{
        const rule =  this.getFirstRuleForComponentByType(component, ValidationRuleType.MAX);
        if (rule && rule.params) {
            const maxValue = parseFloat(rule.params);
            return isNaN(maxValue) ? defaultValue : maxValue;
        }
        return defaultValue;
    }

    static getMinValue(component: BaseFormComponent, defaultValue: number): number {
        const rule =  this.getFirstRuleForComponentByType(component, ValidationRuleType.MIN);
        if (rule && rule.params) {
            const minValue = parseFloat(rule.params);
            return isNaN(minValue) ? defaultValue : minValue;
        }
        return defaultValue;
    }

    private static getFirstRuleForComponentByType(component: BaseFormComponent, type: ValidationRuleType) {
        if (!component ||
            !component.validationRules ||
            component.validationRules.length === 0 ||
            !type) {
            return undefined;
        }
        return component.validationRules.find((rule: ValidationRule) => rule.type === type && rule.level !== ValidationRuleLevel.WARNING);
    }

    private static hasValue(value: any): boolean {
        const isEmpty = value === null || value === undefined ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0);
        return !isEmpty;
    }

    private static isNumeric(value: any): boolean {
        return !isNaN(value) && !isNaN(parseFloat(value));
    }

}